import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Representa uma mensagem que deve ser exibida no format de toast.
 *
 */
export type ToastMessage = {
    id?: number | null;
    title?: string | null;
    message: string;
    nextScreen?: boolean;
    severity?: 'info' | 'error' | 'success' | 'alert' | null;
    duration?: number;
};

/**
 * Serviço responsável por gerenciar as mensagens de toast na aplicação.
 *
 * @example
 * ```typescript
 * this.toastService.addMessage({ message: 'Mensagem de teste' });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
    private readonly messageSubject = new BehaviorSubject<ToastMessage | null>(
        null,
    );
    private readonly message$ = this.messageSubject.asObservable();

    /**
     * Adiciona uma nova mensagem de toast à fila de mensagens.
     *
     * @param message Mensagem a ser exibida no toast.
     */
    public addMessage(message: ToastMessage): void {
        if (!message.id) {
            message.id = Math.floor(Math.random() * 10000);
        }

        this.messageSubject.next(message);
    }

    public removeMessage(): void {
        this.messageSubject.next(null);
    }

    public onMessageAdded(): Observable<ToastMessage | null> {
        return this.message$;
    }

    public error(
        message: string,
        title: string | null = null,
        timeout: number = 5000,
    ) {
        this.addMessage({
            title: title,
            message: message,
            severity: 'error',
            duration: timeout,
        });
    }

    public success(
        message: string,
        title: string | null = null,
        timeout: number = 5000,
    ) {
        this.addMessage({
            title: title,
            message: message,
            severity: 'success',
            duration: timeout,
        });
    }

    public info(
        message: string,
        title: string | null = null,
        timeout: number = 5000,
    ) {
        this.addMessage({
            title: title,
            message: message,
            severity: 'info',
            duration: timeout,
        });
    }

    public alert(
        message: string,
        title: string | null = null,
        timeout: number = 5000,
    ) {
        this.addMessage({
            title: title ?? '',
            message: message,
            severity: 'alert',
            duration: timeout,
        });
    }
}
